// Which thread the talent page's detail panel shows, reconciling two masters:
//
//   the URL   — `?project=` preselects the engagement a link arrived pointing
//               at, so coming from a search or a project opens THAT thread
//               rather than an unexpanded list.
//   the reader — once they click around, their choice wins. A URL that kept
//               re-asserting itself would fight them on every toggle.
//
// The subtlety (Monday 3187516446): "the URL wins once" cannot mean "the first
// URL wins forever". The talent page is one route, so hopping `?project=A` →
// `?project=B` re-renders the SAME component instance — a `useState` lazy
// initializer never re-runs, and the panel kept showing project A under B's
// URL. The fix is to remember WHICH `?project=` the reader's choice was made
// under: while the URL still says that, the choice stands (including "closed");
// the moment the URL names a different project, the selection re-seeds from it.

/** The reader's explicit choice, stamped with the `?project=` it was made
 *  under. `from` is the URL's param at the time of the choice (undefined when
 *  there was none); `id` is the chosen thread, null for "panel closed". */
export interface ThreadSelection {
  from: string | undefined;
  id: string | null;
}

/** Rows only as this module needs them: a thread id, and the project it
 *  belongs to (null on legacy rows that predate project linkage). */
export interface SelectableThread {
  id: string;
  projectId: string | null;
}

/** The thread `?project=` points at, or null when the param is absent or names
 *  a project this talent has no thread for. */
export function seedThreadId(
  threads: readonly SelectableThread[],
  projectId: string | undefined,
): string | null {
  if (projectId === undefined) return null;
  return threads.find((t) => t.projectId === projectId)?.id ?? null;
}

/** The id the detail panel should show NOW: the reader's choice while the URL
 *  still matches the one it was made under, the URL's seed otherwise. */
export function resolveSelectedThreadId(
  selection: ThreadSelection,
  threads: readonly SelectableThread[],
  projectId: string | undefined,
): string | null {
  return selection.from === projectId ? selection.id : seedThreadId(threads, projectId);
}
